import { toIsoDateFr } from "@utilities/functions";
import { Plan } from "@utilities/types";
import { FC, useMemo } from "react";

interface Props {
    plans: Plan[];
    monthlyRevenue: number;
    nextInvoiceDate: Date;
}

export const MonthlyRevenueProgression:FC<Props> = ({ 
    plans,
    monthlyRevenue,
    nextInvoiceDate,
})=> {

    const lastThreshold = useMemo(()=> (
        plans.length > 0 ? plans[plans.length - 1].seuil : 0
    ), [plans]);

    const currentPlan = useMemo(() => {
        if (plans.length === 0 || monthlyRevenue === 0) return null;
        return plans.find(plan => monthlyRevenue <= plan.seuil) || plans[plans.length - 1];
    }, [plans, monthlyRevenue]);

    const nextInvoiceAmount = useMemo(() => (
       currentPlan ? Math.round(monthlyRevenue * currentPlan.rate) : 0
    ), [monthlyRevenue, currentPlan]);

    const progress = useMemo(() => {
        return monthlyRevenue >= lastThreshold
        ? 100
        : Math.min((monthlyRevenue / lastThreshold) * 100, 100);
    }, [lastThreshold, monthlyRevenue]);

    return (
        <div className="bg-white p-6 rounded-xl shadow border border-grey-100">
            <h2 className="text-xl font-bold text-secondary mb-4">📈 Suivi du chiffre d'affaire</h2>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
                <div>
                    <p className="text-md font-semibold text-gray-800">{currentPlan?.label}</p>
                    <p className="text-sm text-primary">Taux appliqué : <span className="font-semibold">{((currentPlan?.rate || 0) * 100).toFixed(1)}%</span></p>
                </div>

                <div className="mt-3 sm:mt-0">
                    <p className="text-sm text-gray-600">Prochaine facture : <span className="font-medium">{toIsoDateFr(nextInvoiceDate)}</span></p>
                    <p className="text-sm text-primary font-semibold mt-1">
                        💰 À payer : {nextInvoiceAmount.toLocaleString()} FCFA
                    </p>
                </div>
            </div>

            <div className="mt-2">
                <p className="text-sm font-medium text-gray-600 mb-1">
                    Chiffre d'affaires mensuel : <span className="text-gray-800">{monthlyRevenue.toLocaleString()} FCFA</span>
                </p>
                <div className="w-full h-4 rounded-full bg-blue-100 overflow-hidden shadow-inner">
                    <div
                        className="h-full bg-primary transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>
        </div>
    )
}
